import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { List, ListItemButton, ListItemAvatar, ListItemText, Avatar, ListSubheader } from '@mui/material'

import { TfiRulerAlt2 } from 'react-icons/tfi'
import { IoLocationOutline } from 'react-icons/io5'
import { LuToilet } from 'react-icons/lu'
import { MdPersonPin } from 'react-icons/md'
import Button from '../../../../components/common/Button/index'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
import Input from '../../../../components/common/Input/index'
import Select from '../../../../components/common/Select/index'
import Modal from '../../../../components/layout/Modal/index'
import useProperty from '../../hooks/useProperty.js'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import Map from '../../../../components/common/Map/Map'
import ProximityMap from '../../../../components/common/Map/ProximityMap'
import { formatDateDashed, formatTime, formatDateSlash } from '../../../../shared/utils/dateUtils'
import useAppSelector from '../../../../state/redux/hooks/index'
import { savedProperties } from '../../../../state/redux/actions/propertyActions'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { IoCloudDownloadOutline } from 'react-icons/io5'
import { IoIosArrowDown } from 'react-icons/io'
import { useDispatch } from 'react-redux'

import MiniHeader from '../../../../components/layout/MiniHeader'

const images = [
  '/assets/images/house.jpg',
  '/assets/images/home.jpg',
  '/assets/images/cover.jpg',
  '/assets/images/house.jpg',
  '/assets/images/home.jpg',
  '/assets/images/cover.jpg',
]

const PropertyDetail = () => {
  const { propertyId } = useParams()
  const dispatch = useDispatch()
  const {
    property,
    loading: propertyLoading,
    error: propertyError,
    getProperty,
    patchProperty,
    approveProperty,
    declineProperty,
  } = useProperty(propertyId)
  const { saved, loading: savedLoading, error: savedError } = useAppSelector(state => state.property)
  const [propertySavings, setPropertySavings] = useState([])
  const [activeTab, setActiveTab] = useState('photos')
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDeclineMode, setIsDeclineMode] = useState(false)
  const [message, setMessage] = useState('')
  const maxLength = 20
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    type: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    images: '',
  })

  const error = propertyError || savedError
  const loading = propertyLoading || savedLoading

  const handleFetch = useCallback(() => {
    getProperty(propertyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId])

  useEffect(() => {
    dispatch(savedProperties())
  }, [dispatch])

  const savedForCurrent = saved.filter(property => property.propertyId._id === propertyId)

  useEffect(() => {
    if (propertyId) {
      handleFetch()
    }
  }, [propertyId, handleFetch])

  const tabs = [
    { id: 'photos', label: 'Photos' },
    { id: 'videos', label: 'Videos' },
    { id: '3D Virtual', label: '3D Virtual' },
    { id: 'map', label: 'Map' },
  ]

  useEffect(() => {
    if (property && property._id === propertyId) {
      setFormData({
        title: property.property_title || '',
        description: property.description || '',
        location: property.locality || '',
        type: property.property_type || '',
        price: property.property_price || '',
        bedrooms: property.bedroom || '',
        bathrooms: property.bathroom || '',
        images: (property.images || []).join('\n'),
      })
    }
  }, [property, propertyId])

  const handleSave = async e => {
    e.preventDefault()
    try {
      const updatedData = {
        property_title: formData.title,
        description: formData.description,
        locality: formData.location,
        listing_type: formData.type,
        property_price: parseFloat(formData.price),
        bedroom: parseInt(formData.bedrooms),
        bathroom: parseInt(formData.bathrooms),
        images: formData.images
          .split('\n')
          .map(url => url.trim())
          .filter(url => url),
      }

      const result = await patchProperty(propertyId, updatedData)
      console.log('Update successful:', result)
      setIsEditMode(false)
    } catch (error) {
      console.error('Update failed:', error)
    }
  }

  const handleApprove = async () => {
    try {
      await approveProperty({ propertyId, propertyStatus: 'LIVE' })
      await handleFetch() // Refresh data after update
    } catch (error) {
      console.error('Approval failed:', error)
    }
  }

  const handleDecline = async () => {
    if (!message.trim()) {
      alert('Please enter a valid decline reason')
      return
    }
    try {
      await declineProperty(propertyId, {
        status: 'DECLINED',
        reason: message,
      })
      setIsDeclineMode(false)
      setMessage('') // Clear message
      await handleFetch()
    } catch (error) {
      console.error('Decline failed:', error)
    }
  }

  // console.log('agent name:', property.agentId.full_name)
  return (
    <>
      <MiniHeader
        title="Property Details"
        onEdit={() => setIsEditMode(prev => !prev)}
        onApprove={handleApprove}
        onDecline={() => setIsDeclineMode(prev => !prev)}
        onRemove={() => console.log('remove')}
      />
      <div className="ml-[250px] p-8 w-[1040px] justify-center items-center h-full mb-4">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && !property && (
          <Card variant="outlined" className="text-center p-8 border border-border rounded-md max-w-md w-full">
            <Typography variant="body" className=" text-secondary ">
              Property not found. Add a new property to get started.
            </Typography>
            <Typography variant="body" className="text-secondary">
              No property found with ID: {propertyId || 'Invalid ID'}
            </Typography>
          </Card>
        )}

        {!loading && !error && property && (
          <>
            {/* cover_image */}
            <Card variant="outlined" className="">
              <div
                className="flex flex-col justify-between h-64 bg-cover bg-center rounded-lg m-0 text-light"
                style={{ backgroundImage: `url("/assets/images/house.jpg")` }}
              >
                {/* property title */}
                <div className="flex flex-col px-4">
                  <div className="flex justify-between py-2">
                    <Typography variant="h3" className=" text-light">
                      {property.property_title}
                    </Typography>
                    <div className="flex flex-col items-center">
                      <MdPersonPin className="text-3xl text-center" />
                      <Typography variant="h5" className="text-light">
                        {property.agentId.full_name}
                      </Typography>
                    </div>
                  </div>
                  <div className=" bg-teal-300/10 w-20">
                    <Typography variant="h5" className="p-2 text-light">
                      Request
                    </Typography>
                  </div>
                </div>
                <div className="flex flex-col px-2">
                  <div className=" text-white px-1">
                    <Typography variant="h4">{property.property_type}</Typography>
                  </div>
                  <div className="flex gap-1">
                    <Typography className="h5">{property.price_prefix}</Typography>
                    <Typography className="h5">{property?.property_price?.toLocaleString()}</Typography>
                    <Typography className="h5">/{property.payment_frequency}</Typography>
                  </div>
                  <div className="flex justify-between">
                    <div className="flex gap-1">
                      <IoLocationOutline className="text-primary text-2xl" />
                      <Typography variant="h5">{property.locality}</Typography>
                    </div>
                    <div className="bg-dark rounded-md">
                      <Typography variant="h4" className="p-2">
                        {formatTime(property.createdAt)}
                      </Typography>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <div className="flex gap-1">
                      <i className="fa-solid fa-bed" />
                      <Typography variant="caption" className="text-white">
                        {property.bedroom}
                      </Typography>
                    </div>
                    <div className="flex gap-1">
                      <LuToilet />
                      <Typography variant="caption" className="text-white">
                        {property.bathroom}
                      </Typography>
                    </div>
                    <div className="flex gap-1 whitespace-nowrap ">
                      <TfiRulerAlt2 className="mb-1 text-primary" />
                      <Typography variant="caption" className="text-white">
                        {property.building_unit_size}
                        {property.building_unit_size_unit}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            {/* tabs header */}
            <div className="flex justify-between w-full ">
              {/* media tabs */}
              <div className="flex ">
                <div>
                  {/* image grid */}
                  {activeTab === 'photos' && images.length > 0 && (
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mt-4">
                      {(isExpanded ? images : images.slice(0, 3)).map((image, index) => (
                        <div key={index} className="relative aspect-video overflow-hidden rounded">
                          <img
                            src={image}
                            alt="Property"
                            className="w-full h-full object-cover hover:scale-105 rounded"
                          />
                          {isExpanded && images && index === images.length - 1 && (
                            <div
                              className="absolute bottom-2 right-2 p-4 bg-white flex items-center justify-center cursor-pointer"
                              onClick={() => setIsExpanded(false)}
                            >
                              <span className="text-dark text-lg font-semibold flex items-center">
                                <i className="fa-solid fa-chevron-up"></i>
                              </span>
                            </div>
                          )}
                          {!isExpanded && images.length > 3 && index === 2 && (
                            <div
                              className="absolute bottom-2 right-2 p-2 bg-white flex items-center justify-center cursor-pointer"
                              onClick={() => setIsExpanded(true)}
                            >
                              <span className="text-dark text-lg font-semibold flex items-center gap-2">
                                See all {images.length} photos
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Map */}
                  {activeTab === 'map' && property.latitude && property.longitude && (
                    <div className="mt-4 w-full">
                      <Map center={{ lat: property.latitude, lng: property.longitude }} />
                    </div>
                  )}
                  <div className="flex gap-6 pt-2 justify-between">
                    {/* property type */}
                    <div className="p-2">
                      <Typography variant="h2" className="text-dark whitespace-nowrap">
                        Type:
                        {property.property_type.toLowerCase()}
                      </Typography>
                    </div>
                    {/* utilities */}
                    <div className="flex gap-2">
                      <div>
                        <Button className="py-[10px] gap-1">
                          <LuToilet />
                          <Typography variant="caption" className="text-white">
                            {property.bathroom}
                          </Typography>
                          baths
                        </Button>
                      </div>
                      <div>
                        <Button className="py-[10px] gap-1">
                          <i className="fa-solid fa-bed" />
                          <Typography variant="caption" className="text-white">
                            {property.bedroom}
                          </Typography>
                          beds
                        </Button>
                      </div>
                      <div>
                        <Button className="py-[10px] gap-1">
                          <TfiRulerAlt2 className="mb-1 text-white" />
                          <Typography variant="caption" className="text-white">
                            {property.building_unit_size}
                            {property.building_unit_size_unit}
                          </Typography>
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-8 ">
                      {tabs.map(tab => (
                        <button
                          key={tab.id}
                          className={
                            activeTab === tab.id
                              ? 'font-semibold p-3 text-primary border-b border-primary focus:ring-0'
                              : 'shadow-transparent text-gray-700 font-semibold no-underline outline-none'
                          }
                          onClick={() => setActiveTab(tab.id)}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* show property */}

            <>
              <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Card className="p-4 col-span-1 h-full" title={'Property Info'}>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Available Date;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left" className="">
                        {formatDateSlash(property.available_date)}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Pets Allowed;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left" className="">
                        {property.pets_allowed}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Utility Payment;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left" className="">
                        {property.utility_payment}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Property Type;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.property_type}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Structure;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.structure.replace(/_/g, ' ')}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Style;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.style}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Year Built;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.year_built}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Status;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.status}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Listing Type;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.listing_type}
                      </Typography>
                    </div>
                  </Card>
                </div>
                <div>
                  <Card title={'Utilies'} className="p-4 h-full">
                    {property.utilities.map((utility, index) => (
                      <div key={index}>
                        <Typography variant="body" className="mb-2 text-gray-700">
                          {utility}
                        </Typography>
                      </div>
                    ))}
                    {property.utilities.length === 0 && <Typography variant="body">None Listed</Typography>}
                  </Card>
                </div>
                <div className="flex flex-col col-span-1 h-full">
                  <Card title={'Description'} className="p-4 ">
                    <Typography variant="body">{property.description}</Typography>
                  </Card>

                  <Card className="my-[4px] p-4 ">
                    <div className="flex justify-between">
                      <Typography variant="h4">{'Proximity'}</Typography>
                      <Typography variant="body">{`${property.proximity.length} places nearby`}</Typography>
                    </div>
                    <ProximityMap
                      center={{
                        lat: property.latitude,
                        lng: property.longitude,
                      }}
                      proximity={property.proximity}
                    />
                  </Card>
                </div>
              </div>
              <div className="my-4 grid md:grid-cols-12 gap-4">
                <div className="col-span-3">
                  <Card className="h-full">
                    {savedForCurrent > 0 && (
                      <List>
                        <div className="flex justify-between mt-2">
                          <ListSubheader className="text-black text-base">
                            <strong>Saved</strong>
                          </ListSubheader>
                          <ListSubheader className="text-black text-base">
                            <strong>{saved.filter(item => item.propertyId._id === propertyId).length} today</strong>
                          </ListSubheader>
                        </div>
                        {savedForCurrent.map(save => (
                          <ListItemButton key={save._id} className="">
                            <ListItemAvatar>
                              <Avatar alt="Profile Picture" src={'/assets/images/house.jpg'} />
                            </ListItemAvatar>
                            <div className="flex flex-col">
                              <ListItemText>{save.userId.full_name}</ListItemText>
                              <ListItemText>
                                Joined, {formatDistanceToNow(new Date(property.createdAt), { addSuffix: true })}
                              </ListItemText>
                            </div>
                          </ListItemButton>
                        ))}
                      </List>
                    )}
                  </Card>
                </div>
                <div className="col-span-4">
                  <Card title={'Features'} className="p-4 col-span-2 h-full">
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Spefication;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.specification.replace(/_/g, ' ')}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Area Size;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.listing_type}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Fire Place;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.fire_place}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Entry Floor;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.entry_floor}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Parking Lot;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.parking_lot}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Parking Size;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.listing_type}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Building Unit;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.building_unit_size}
                        {property.building_unit_size_unit}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Renovation;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.revisionHistory.length > 0 ? property.revisionHistory.length : 'None'}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Appliances;</strong>
                      </Typography>
                      <Typography variant="body" color="secondary" align="left">
                        {property.appliances_details ? (
                          <>
                            {Object.keys(property.appliances_details).map(
                              category =>
                                property.appliances_details[category].length > 0 && (
                                  <span key={category}>
                                    {property.appliances_details[category].join(', ')}
                                    <br />
                                  </span>
                                )
                            )}
                          </>
                        ) : (
                          <span>No appliance details available.</span>
                        )}
                      </Typography>
                    </div>
                    <div className="grid grid-cols-2">
                      <Typography variant="body" color="secondary" className="">
                        <strong>Ammenities;</strong>
                      </Typography>
                      <div className="flex flex-col">
                        {property.unit_amenities.map((ammenity, index) => (
                          <Typography key={index} variant="body" className=" text-gray-700 whitespace-nowrap">
                            {ammenity}
                          </Typography>
                        ))}
                      </div>
                      {property.unit_amenities.length === 0 && <Typography variant="body">None Listed</Typography>}
                    </div>
                  </Card>
                </div>
                <div className="flex flex-col col-span-5">
                  <div>
                    <Card title={'Property History'} className="p-4">
                      <div className="flex gap-2">
                        <Button>
                          <div className="flex flex-col">
                            <Typography variant="body" align="center">
                              Property Price
                            </Typography>
                            <Typography variant="body" align="center">
                              {property.price_prefix}
                              {property.property_price}
                            </Typography>
                          </div>
                        </Button>
                        <Button>
                          <div className="flex flex-col">
                            <Typography variant="body" align="center">
                              Date Posted
                            </Typography>
                            <Typography variant="body" align="center">
                              {formatDateDashed(property.createdAt)}
                            </Typography>
                          </div>
                        </Button>
                        <Button size="small">
                          <div className="flex flex-col">
                            <Typography variant="body" align="center">
                              Total Lessee
                            </Typography>
                            <Typography variant="body" align="center">
                              {property.total_lessee}
                            </Typography>
                          </div>
                        </Button>
                      </div>
                    </Card>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Card title={'Trakheesi Permit'} className="p-4 whitespace-nowrap">
                        <Typography color="danger" align="left" className="py-2">
                          {property.trakheesi_permit}
                        </Typography>
                      </Card>
                    </div>
                    <div>
                      <Card title={'Property Document'} className="p-2 md:p-4 whitespace-nowrap">
                        <Typography variant="h4"></Typography>
                        <div>
                          <Button to="/reports" className="flex w-full gap-2">
                            <IoCloudDownloadOutline />
                            Download
                          </Button>
                        </div>
                      </Card>
                    </div>
                    {property.status === 'DECLINED' && (
                      <div className="col-span-full bg-red-100 rounded-lg px-2 py-4">
                        <Typography className="text-danger">
                          Sorry, we couldn&apos;t accept your property due to blurry pictures. Please upload clearer
                          images.
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>

            {/* edit property form*/}
            {isEditMode && (
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="flex justify-center items-center">
                  <Card className="px-8 py-3 lg:py-4">
                    <div className="">
                      <form className="grid grid-cols-2 gap-4" onSubmit={handleSave}>
                        <div className="">
                          <label htmlFor="title" className="block font-medium text-gray-700">
                            Title
                          </label>
                          <Input
                            id="title"
                            name="title"z
                            value={formData.title}
                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                            required
                          />
                        </div>

                        <div className="col-span-2">
                          <label htmlFor="description" className="block font-medium text-gray-700">
                            Description
                          </label>
                          <Input
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                          />
                        </div>

                        <div className="">
                          <label htmlFor="location" className="block font-medium text-gray-700">
                            Location
                          </label>
                          <Input
                            id="location"
                            name="location"
                            value={formData.location}
                            onChange={e => setFormData({ ...formData, location: e.target.value })}
                            required
                          />
                        </div>

                        <div className="">
                          <label htmlFor="type" className="block font-medium text-gray-700">
                            Type
                          </label>
                          <Select
                            id="type"
                            name="type"
                            value={formData.type}
                            icon={<IoIosArrowDown />}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            options={[
                              { value: 'rent', label: 'Rent' },
                              { value: 'sale', label: 'Sale' },
                              { value: 'short-let', label: 'Short-Let' },
                            ]}
                            required
                          />
                        </div>

                        <div className="">
                          <label htmlFor="price" className="block font-medium text-gray-700">
                            Price
                          </label>
                          <Input
                            id="price"
                            name="price"
                            value={formData.price}
                            onChange={e => setFormData({ ...formData, price: e.target.value })}
                            type="number"
                            required
                          />
                        </div>

                        <div className="">
                          <label htmlFor="bedrooms" className="block font-medium text-gray-700">
                            Bedrooms
                          </label>
                          <Input
                            id="bedrooms"
                            name="bedrooms"
                            value={formData.bedrooms}
                            onChange={e => setFormData({ ...formData, bedrooms: e.target.value })}
                            type="number"
                            required
                          />
                        </div>

                        <div className="">
                          <label htmlFor="bathrooms" className="block font-medium text-gray-700">
                            Bathrooms
                          </label>
                          <Input
                            id="bathrooms"
                            name="bathrooms"
                            value={formData.bathrooms}
                            onChange={e => setFormData({ ...formData, bathrooms: e.target.value })}
                            type="number"
                            required
                          />
                        </div>

                        <div className="">
                          <label htmlFor="images" className="block font-medium text-gray-700">
                            Images
                          </label>
                          <textarea
                            id="images"
                            name="images"
                            value={formData.images}
                            onChange={e => setFormData({ ...formData, images: e.target.value })}
                            placeholder="Enter image URLs (one per line)"
                            rows="3"
                          />
                        </div>
                        <div className="flex justify-end gap-4">
                          <Button variant="primary" className="mr-2" type="submit" disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button variant="secondary" onClick={() => setIsEditMode(false)} type="button">
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </div>
                  </Card>
                </div>
              </div>
            )}
            {/* decline property form*/}
            {isDeclineMode && (
              <Modal
                title="Comment"
                isOpen={isDeclineMode}
                onClose={() => setIsDeclineMode(false)}
                className="boder-none"
              >
                <div>
                  <Card variant="outlined" className="mt-[8px] h-[62px]">
                    <textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      className="flex-grow p-3 overflow-y-hidden w-full rounded-md resize-none focus:outline-black"
                      placeholder="Ex. Please add the 360-degree virtual tour embedded code to this property for upload"
                    ></textarea>
                  </Card>
                  <div className="flex justify-end">
                    <Typography variant="body" className="">
                      {message.length}/{maxLength}
                    </Typography>
                  </div>
                  <div className="flex gap-4 justify-between mt-4">
                    <Button onClick={handleDecline}>Send</Button>
                    <Button variant="danger" onClick={() => setIsDeclineMode(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default PropertyDetail
